import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Loader2, ShieldOff } from 'lucide-react';
import { useAuth } from './useAuth';
import type { PermissionCode } from './AuthContext';

function Booting() {
  return (
    <div className="flex min-h-svh items-center justify-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
    </div>
  );
}

/**
 * Everything but /login sits behind this. No token, or a token `/auth/me`
 * rejected, sends the user to the login page carrying the path they wanted so
 * signing in lands them where they were headed.
 */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { user, token, isLoading } = useAuth();
  const location = useLocation();

  if (token && isLoading && !user) {
    return <Booting />;
  }
  if (!user) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }
  return <>{children ?? <Outlet />}</>;
}

/**
 * Route-level gate (plan §8d). Every code in `codes` is required — a page
 * code, and for the pages made of money the costs read beside it. Renders a
 * plain "No access" page rather than redirecting: a bookmarked or shared URL
 * should say why it does nothing, not bounce somewhere else. The matching nav
 * item is hidden by the same codes, so this page is only reached by typing
 * or following an old link.
 */
export function RequirePermission({ codes, children }: {
  codes: PermissionCode | PermissionCode[];
  children?: ReactNode;
}) {
  const { can } = useAuth();
  const required = Array.isArray(codes) ? codes : [codes];
  const missing = required.filter((code) => !can(code));
  if (missing.length) {
    return <NoAccess codes={missing} />;
  }
  return <>{children ?? <Outlet />}</>;
}

export function NoAccess({ codes, hint }: { codes?: PermissionCode[]; hint?: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 py-24 text-center">
      <ShieldOff className="h-6 w-6 text-muted-foreground" />
      <p className="text-sm font-medium">No access</p>
      <p className="max-w-sm text-xs text-muted-foreground">
        {hint ?? (
          <>
            This page needs the{' '}
            {codes?.length
              ? codes.map((code, i) => (
                <span key={code}>
                  {i > 0 && (i === codes.length - 1 ? ' and ' : ', ')}
                  <code className="font-mono">{code}</code>
                </span>
              ))
              : 'required'}{' '}
            permission{codes && codes.length > 1 ? 's' : ''}. Ask an administrator to grant {codes && codes.length > 1 ? 'them' : 'it'}.
          </>
        )}
      </p>
    </div>
  );
}

/**
 * Control-level gate. Wraps a mutation control and renders nothing when the
 * user lacks the code — hidden, never greyed out (plan §4). Use the SAME code
 * the server route requires (docs/PERMISSIONS.md), so the UI and the gate can
 * never disagree.
 */
export function Can({ code, children }: { code: PermissionCode; children: ReactNode }) {
  const { can } = useAuth();
  if (!can(code)) {
    return null;
  }
  return <>{children}</>;
}
